import { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styles from './InscricaoConfig.module.css';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Botao from '../../components/Botao';
import { mdiContentSave, mdiLoading, mdiCheckCircle, mdiAlertCircle } from '@mdi/js';
import { toast } from 'react-toastify';
import Icon from '@/components/Icon';

interface CategoriaInfo {
    nome: string;
    descricao: string;
}

interface FrequenciaInfo {
    nome: string;
    descricao: string;
}

interface Preferencias {
    [categoria: string]: string;
}

interface ConfigData {
    nome: string;
    email: string;
    preferencias: Preferencias;
    categorias: Record<string, CategoriaInfo>;
    frequencias: Record<string, FrequenciaInfo>;
}

const InscricaoConfigPage = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email');

    const [preferencias, setPreferencias] = useState<Preferencias>({});
    const [salvando, setSalvando] = useState(false);
    const [sucesso, setSucesso] = useState(false);

    // Buscar configurações atuais
    const { isLoading, data, error } = useQuery(
        ['inscricao-config', uuid, email],
        async () => {
            if (!email) {
                throw new Error('Email é obrigatório');
            }
            const response = await axios.get(`/api/inscricao/org/${uuid}/config`, {
                params: { email },
            });
            return response.data.result as ConfigData;
        },
        {
            enabled: !!uuid && !!email,
            onSuccess: (data) => {
                setPreferencias(data.preferencias || {});
            },
            onError: (err: any) => {
                const msg = err?.response?.data?.msg || 'Erro ao carregar configurações';
                toast.error(msg);
            },
        }
    );

    // Salvar configurações
    const salvarMutation = useMutation(
        async (novasPreferencias: Preferencias) => {
            if (!email) {
                throw new Error('Email é obrigatório');
            }
            setSalvando(true);
            const response = await axios.put(
                `/api/inscricao/org/${uuid}/config`,
                { preferencias: novasPreferencias },
                { params: { email } }
            );
            return response.data;
        },
        {
            onSuccess: () => {
                setSalvando(false);
                setSucesso(true);
                toast.success('Preferências salvas com sucesso!');
                setTimeout(() => setSucesso(false), 5000);
            },
            onError: (err: any) => {
                setSalvando(false);
                const msg = err?.response?.data?.msg || 'Erro ao salvar configurações';
                toast.error(msg);
            },
        }
    );

    const handleChange = (categoria: string, valor: string) => {
        setPreferencias({ ...preferencias, [categoria]: valor });
    };

    const handleSalvar = () => {
        salvarMutation.mutate(preferencias);
    };

    if (!email) {
        return (
            <div className={styles.container}>
                <VBox center style={{ width: '100%' }}>
                    <div className={styles.errorBox}>
                        <Icon path={mdiAlertCircle} size={2} color="white" />
                        <h2>Email não informado</h2>
                        <p>Por favor, acesse o link completo enviado no email.</p>
                    </div>
                </VBox>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.container}>
                <VBox center style={{ width: '100%' }}>
                    <div className={styles.loading}>Carregando...</div>
                </VBox>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <VBox center style={{ width: '100%' }}>
                    <div className={styles.errorBox}>
                        <Icon path={mdiAlertCircle} size={2} color="white" />
                        <h2>Erro ao carregar configurações</h2>
                        <p>Verifique se o link está correto ou entre em contato com o suporte.</p>
                    </div>
                </VBox>
            </div>
        );
    }

    const categorias = data?.categorias || {};
    const frequencias = data?.frequencias || {};

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <img
                        src="/terachat-300.png"
                        alt="Terachat"
                        className={styles.logo}
                    />
                    <h1 className={styles.title}>Preferências de Comunicação</h1>
                    {data?.nome && (
                        <p className={styles.subtitle}><strong>{data.nome}</strong></p>
                    )}
                    {data?.email && (
                        <p className={styles.email}>{data.email}</p>
                    )}
                </div>

                <div className={styles.mainCard}>
                    <p className={styles.intro}>
                        Escolha a frequência de emails para cada categoria:
                    </p>

                    <Grid>
                        {Object.entries(categorias).map(([categoriaKey, categoriaInfo]) => (
                            <GridItem key={categoriaKey} sm={12} md={6}>
                                <div className={styles.categoryCard}>
                                    <h3 className={styles.categoryTitle}>{categoriaInfo.nome}</h3>
                                    <p className={styles.categoryDesc}>{categoriaInfo.descricao}</p>

                                    <div className={styles.radioGroup}>
                                        {Object.entries(frequencias).map(([frequenciaKey, frequenciaInfo]) => (
                                            <label key={frequenciaKey} className={styles.radioCompact}>
                                                <input
                                                    type="radio"
                                                    name={categoriaKey}
                                                    value={frequenciaKey}
                                                    checked={preferencias[categoriaKey] === frequenciaKey}
                                                    onChange={(e) => handleChange(categoriaKey, e.target.value)}
                                                />
                                                <span className={styles.radioText} title={frequenciaInfo.descricao}>
                                                    {frequenciaInfo.nome}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </GridItem>
                        ))}
                    </Grid>

                    <div className={styles.actions}>
                        <Botao
                            variant="primary"
                            icon={salvando ? mdiLoading : sucesso ? mdiCheckCircle : mdiContentSave}
                            onClick={handleSalvar}
                            disabled={salvando || sucesso}
                        >
                            {salvando ? 'Salvando...' : sucesso ? 'Salvo!' : 'Salvar Preferências'}
                        </Botao>
                    </div>

                    <p className={styles.footerText}>
                        Suas preferências serão aplicadas imediatamente.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default InscricaoConfigPage;
